import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';

/* ── Role-based nav config ───────────────────────────────────────────────── */
const NAV_BY_ROLE = {
  admin: [
    {
      to: '/increment-cycles',
      label: 'Increment Cycles',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      ),
    },
    {
      to: '/recommendations',
      label: 'Recommendations',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
      ),
    },
    {
      to: '/users',
      label: 'User Management',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      ),
    },
  ],
  hod: [
    {
      to: '/recommendations',
      label: 'HOD Recommendations',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
      ),
    },
  ],
  hr: [
    {
      to: '/recommendations',
      label: 'HR Recommendations',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
      ),
    },
  ],
  management: [
    {
      to: '/recommendations',
      label: 'Management Approvals',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 11 12 14 22 4"/>
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
        </svg>
      ),
    },
  ],
};

export default function Navbar() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { user }  = useSelector(s => s.auth);
  const [menuOpen, setMenuOpen] = useState(false);

  const role    = user?.role || localStorage.getItem('user_role') || 'admin';
  const navItems = NAV_BY_ROLE[role] || NAV_BY_ROLE.admin;

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  return (
    <nav style={{
      background: 'var(--navy)', padding: '0 32px', height: 60,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)', position: 'sticky', top: 0, zIndex: 100,
    }}>
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 32, height: 32, background: 'var(--magenta)',
          clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
        }} />
        <div>
          <div style={{ color: 'var(--white)', fontWeight: 700, fontSize: 15, letterSpacing: 1 }}>
            PRO<span style={{ color: 'var(--magenta)' }}>A</span>CTIVE
          </div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 9, letterSpacing: 0.5 }}>
            Increment Management System
          </div>
        </div>
      </div>

      {/* Nav links */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {navItems.map(item => (
          <NavLink key={item.to} to={item.to} style={({ isActive }) => navLinkStyle(isActive)}>
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </div>

      {/* Profile */}
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)', border: 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--white)',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </button>

        {menuOpen && (
          <div style={{
            position: 'absolute', right: 0, top: 44,
            background: 'var(--white)', borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-lg)', minWidth: 200, zIndex: 200,
            border: '1px solid var(--grey-200)',
          }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--grey-100)' }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{user?.name}</div>
              <div style={{ color: 'var(--grey-400)', fontSize: 12 }}>{user?.email}</div>
              {/* Role badge */}
              <div style={{ marginTop: 6 }}>
                <span style={{
                  padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                  ...ROLE_BADGE_STYLE[role],
                }}>
                  {ROLE_BADGE_LABEL[role] || role.toUpperCase()}
                </span>
              </div>
            </div>
            <button
              onClick={() => { setMenuOpen(false); navigate('/change-password'); }}
              style={dropdownItemStyle}
            >
              Change Password
            </button>
            <button onClick={handleLogout} style={{ ...dropdownItemStyle, color: 'var(--danger)' }}>
              Logout
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}

/* ── Styles ────────────────────────────────────────────────────────────────── */
const navLinkStyle = (isActive) => ({
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 6,
  color: isActive ? 'var(--white)' : 'rgba(255,255,255,0.65)',
  background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
  fontWeight: isActive ? 600 : 400, fontSize: 13.5, transition: 'all 0.15s',
  textDecoration: 'none',
});

const dropdownItemStyle = {
  display: 'block', width: '100%', padding: '10px 16px',
  background: 'none', border: 'none', textAlign: 'left',
  cursor: 'pointer', fontSize: 13.5, color: 'var(--grey-700)',
  transition: 'background 0.12s',
};

const ROLE_BADGE_STYLE = {
  admin:      { background: '#F3F4F6', color: '#374151' },
  hod:        { background: '#EFF6FF', color: '#1D4ED8' },
  hr:         { background: '#F0FFF4', color: '#15803D' },
  management: { background: '#FFF7ED', color: '#C2410C' },
};
const ROLE_BADGE_LABEL = {
  admin: 'ADMIN', hod: 'HOD', hr: 'HR HEAD', management: 'MANAGEMENT',
};
